import React, { useEffect, useState, useCallback } from 'react';
import { Sparkles, Send, Mail, History } from 'lucide-react';
import { streamAI } from '../../utils/streamAI';

const CampaignsTab = ({ password, subscriberCount }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [topic, setTopic] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns?password=${encodeURIComponent(password)}`);
      if (res.ok) setHistory(await res.json());
    } catch { /* noop */ }
  }, [password]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const draft = async () => {
    if (!topic.trim() || drafting) return;
    setDrafting(true);
    setError('');
    setSubject('');
    setBody('');
    let full = '';
    await streamAI('/api/ai/generate', { mode: 'newsletter', prompt: `Write a newsletter about: ${topic}`, password },
      (chunk) => {
        full += chunk;
        const match = full.match(/^SUBJECT:\s*(.+?)(\n|$)/i);
        if (match) {
          setSubject(match[1].trim());
          setBody(full.slice(match[0].length).replace(/^\n+/, ''));
        } else {
          setBody(full);
        }
      },
      () => setDrafting(false),
      (err) => { setError(err); setDrafting(false); });
  };

  const sendTest = async () => {
    setError(''); setStatus('');
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, subject, body, test_email: testEmail })
      });
      const data = await res.json();
      if (res.ok) setStatus(`Test sent to ${data.to} ✓`);
      else setError(data.detail || 'Test failed');
    } catch { setError('Test failed'); }
    setSending(false);
  };

  const sendAll = async () => {
    if (!window.confirm(`Send "${subject}" to all ${subscriberCount} subscribers? This cannot be undone.`)) return;
    setError(''); setStatus('');
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, subject, body })
      });
      const data = await res.json();
      if (res.ok) { setStatus(`Campaign sent to ${data.sent} subscriber${data.sent !== 1 ? 's' : ''} ✓${data.failed ? ` (${data.failed} failed)` : ''}`); fetchHistory(); }
      else setError(data.detail || 'Send failed');
    } catch { setError('Send failed'); }
    setSending(false);
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm";

  return (
    <div data-testid="campaigns-tab">
      <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Campaigns</h1>
      <p className="text-sm text-foreground/60 mb-6">Write (or let ChatGPT draft) a newsletter and send it to your {subscriberCount} subscriber{subscriberCount !== 1 ? 's' : ''}.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="p-4 rounded-2xl border border-primary/30 bg-primary/5">
            <label className="block text-sm font-medium text-foreground mb-2">✦ Draft with AI (ChatGPT)</label>
            <div className="flex space-x-2">
              <input value={topic} onChange={e => setTopic(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') draft(); }}
                placeholder="e.g. Spring harvest is here, new Wild Forest batch" className={inputCls} data-testid="campaign-topic-input" />
              <button onClick={draft} disabled={drafting || !topic.trim()}
                className="px-5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-40 flex items-center space-x-1.5"
                data-testid="campaign-draft-button">
                <Sparkles className="w-4 h-4" /><span>{drafting ? '...' : 'Draft'}</span>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Subject *</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} className={inputCls} data-testid="campaign-subject-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Newsletter body *</label>
            <textarea rows={12} value={body} onChange={e => setBody(e.target.value)}
              placeholder="Write your newsletter, or draft it with AI above. Blank lines create paragraphs — it's sent inside the branded Ricky's Bees email template."
              className={inputCls} data-testid="campaign-body-input" />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@email.com"
              className="flex-1 min-w-[180px] px-4 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" data-testid="campaign-test-email-input" />
            <button onClick={sendTest} disabled={sending || !subject.trim() || !body.trim() || !testEmail.trim()}
              className="px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:border-primary hover:text-primary transition-colors disabled:opacity-40 flex items-center space-x-1.5"
              data-testid="campaign-test-button">
              <Mail className="w-4 h-4" /><span>Send Test</span>
            </button>
            <button onClick={sendAll} disabled={sending || !subject.trim() || !body.trim() || subscriberCount === 0}
              className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40 flex items-center space-x-1.5"
              data-testid="campaign-send-button">
              <Send className="w-4 h-4" /><span>{sending ? 'Sending...' : `Send to ${subscriberCount}`}</span>
            </button>
          </div>
          {status && <p className="text-green-600 text-sm font-medium" data-testid="campaign-status">{status}</p>}
          {error && <p className="text-red-500 text-sm" data-testid="campaign-error">{error}</p>}
        </div>

        <div className="space-y-4">
          <div className="p-5 rounded-2xl border border-border bg-muted/30">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3">Preview</p>
            <div className="bg-[#faf7f2] rounded-xl p-5">
              <p className="font-bold text-foreground mb-3">{subject || <span className="text-foreground/30 italic font-normal">Subject...</span>}</p>
              <div className="bg-white rounded-lg p-4 text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed min-h-[120px]" data-testid="campaign-preview">
                {body || <span className="text-foreground/30 italic">Newsletter body preview...</span>}
              </div>
              <p className="text-[10px] text-foreground/40 mt-3 text-center">Sent inside the branded template with logo, Shop Honey button & unsubscribe link</p>
            </div>
          </div>

          <div className="p-5 rounded-2xl border border-border bg-muted/30">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3 flex items-center"><History className="w-3.5 h-3.5 mr-1.5" />Past campaigns</p>
            {history.length === 0 ? <p className="text-sm text-foreground/40 italic">No campaigns sent yet</p> :
              history.map(c => (
                <div key={c.campaign_id} className="py-2 border-b border-border/50 last:border-0" data-testid={`campaign-history-${c.campaign_id}`}>
                  <p className="text-sm font-medium text-foreground">{c.subject}</p>
                  <p className="text-xs text-foreground/50">
                    {new Date(c.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })} · sent to {c.sent}{c.failed ? ` · ${c.failed} failed` : ''}
                  </p>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignsTab;
